import { filterDOMProps, useId } from '@react-aria/utils';
import { forwardRef } from 'react';

import * as styles from './logo.css';
import { twMerge } from '../utils';

import type { HTMLProps } from '../types';

export type LogoProps = {
  /**
   * Invert the logo's color.
   * @default false
   */
  onBackground?: boolean;
  size?: 'md' | 'sm';
} & HTMLProps<SVGSVGElement>;

export const Logo = forwardRef<SVGSVGElement, LogoProps>((props, ref) => {
  const { onBackground = false, className, size = 'md', ...otherProps } = props;
  const id = useId();
  const fill = onBackground ? '#fff' : '#000';

  return (
    <svg
      fill='transparent'
      role='img'
      aria-labelledby={id}
      viewBox={size === 'sm' ? '0 0 79.877 45' : '0 0 325 44'}
      xmlns='http://www.w3.org/2000/svg'
      className={twMerge(styles.logo(), className)}
      ref={ref}
      {...filterDOMProps(otherProps)}
    >
      <title id={id}>GoodHuman</title>
      <g clipPath='url(#a)'>
        {size === 'sm' ? (
          <path
            shapeRendering='optimizeQuality'
            fillRule='evenodd'
            clipRule='evenodd'
            d='M38.726,21.045 L38.726,42.919 L32.613,42.919 L32.613,37.948 C29.855,41.666 26.137,44 19.783,44 C7.556,44 0,35.13 0,22.238 C0,8.87 8.032,0 20.681,0 C30.806,0 37.723,5.698 38.665,14.603 L31.956,14.603 C31.29,9.416 26.561,6.147 20.387,6.147 C11.932,6.147 6.657,12.26 6.657,21.97 C6.657,31.68 11.932,37.8 20.491,37.8 C27.408,37.8 32.233,33.901 32.717,27.192 L19.247,27.192 L19.247,21.019 L38.726,21.045 Z M79.851,42.92 L79.877,0.96 L73.22,0.96 L73.22,18.702 L52.158,18.702 L52.158,0.96 L45.5,0.96 L45.5,42.92 L52.158,42.92 L52.158,24.874 L73.22,24.874 L73.22,42.918 L79.851,42.92 Z'
            fill={fill}
          />
        ) : (
          <path
            shapeRendering='optimizeQuality'
            fillRule='evenodd'
            clipRule='evenodd'
            d='M38.726 21.045v21.874h-6.113v-4.971C29.855 41.666 26.137 44 19.783 44 7.556 44 0 35.13 0 22.238 0 8.87 8.032 0 20.681 0c10.125 0 17.042 5.698 17.984 14.603h-6.709c-.666-5.187-5.395-8.456-11.569-8.456-8.455 0-13.73 6.113-13.73 15.823S11.932 37.8 20.491 37.8c6.917 0 11.742-3.899 12.226-10.608h-13.47v-6.173l19.479.026Zm33.746 6.648c0 9.598-5.81 15.883-14.802 15.883s-14.76-6.285-14.76-15.883c0-9.597 5.88-15.943 14.75-15.943 8.872 0 14.812 6.416 14.812 15.944Zm-23.198 0c0 6.174 3.182 10.134 8.517 10.134 5.334 0 8.326-3.96 8.326-10.133 0-6.174-3.173-10.186-8.508-10.186-5.335 0-8.335 4.073-8.335 10.186Zm41.19 15.883c8.992 0 14.802-6.285 14.802-15.883 0-9.527-5.93-15.943-14.802-15.943-8.87 0-14.75 6.346-14.75 15.944 0 9.597 5.758 15.882 14.75 15.882Zm.113-5.75c-5.335 0-8.508-3.96-8.508-10.133 0-6.112 3-10.185 8.335-10.185 5.335 0 8.508 4.012 8.508 10.186 0 6.173-3 10.133-8.335 10.133ZM138.77.304V42.92h-6.173v-5.092c-1.557 2.593-4.496 5.75-10.488 5.75-8.032 0-13.246-6.286-13.246-16.005 0-9.51 5.81-15.822 13.791-15.822 5.386 0 8.205 2.395 9.77 5.032V.302h6.346Zm-6.113 27.27c0-6.113-3.458-10.376-8.75-10.376-5.291 0-8.689 4.194-8.689 10.306 0 6.113 3.355 10.445 8.689 10.445 5.335 0 8.75-4.202 8.75-10.376Zm39.945 15.346h6.631l.026-41.96h-6.657v17.742H151.54V.96h-6.658v41.96h6.658V24.874h21.062v18.044Zm39.227-30.572V42.92h-6.112v-5.187a10.745 10.745 0 0 1-9.831 5.516c-6.709 0-10.851-4.496-10.851-11.75V12.312h6.294v18.59c0 4.435 2.162 6.916 6.113 6.916 5.101 0 8.093-4.02 8.093-10.67v-14.81l6.294.009Zm49.75 30.572V21.944c0-6.355-3.899-10.194-10.514-10.194-4.591 0-7.781 1.859-9.51 6.234-1.202-4.254-4.194-6.234-8.811-6.234-3.476 0-6.657 1.141-8.516 5.698v-5.101h-6.295v30.572h6.355V25.48c0-5.274 2.343-8.274 6.597-8.274 3.658 0 5.698 2.213 5.698 6.234v19.48h6.355V25.54c0-5.395 2.326-8.334 6.649-8.334 3.718 0 5.698 2.153 5.698 6.234v19.48h6.294Zm30.261-20.923V42.92h-6.052v-4.617c-2.456 3.96-6.294 5.274-10.004 5.274-6.294 0-9.951-3.77-9.951-9.346 0-5.335 3.458-8.274 9.511-9.113l6.839-.96c2.593-.302 3.83-1.202 3.83-3.355 0-2.818-2.335-4.322-5.992-4.322-4.141 0-6.597 1.919-6.917 5.274h-6c.605-6.234 5.187-10.013 13.012-10.013 7.522.01 11.724 3.485 11.724 10.255Zm-5.974 7.98v-2.222a28.283 28.283 0 0 1-4.548.96l-3.458.475c-3.77.605-5.75 2.101-5.75 4.86 0 2.757 1.859 4.193 5.188 4.193 4.591 0 8.568-3.113 8.568-8.266Zm38.389 12.943V23.137c0-7.488-3.805-11.387-10.67-11.421-4.461 0-7.548 1.59-9.649 5.187v-4.556h-6.355v30.572h6.355V26.561c0-5.637 2.94-9.174 7.669-9.174 4.021 0 6.355 2.456 6.355 6.407V42.92h6.295Z'
            fill={fill}
          />
        )}
      </g>
      <defs>
        <clipPath id='a'>
          <path fill='transparent' d='M0 0h324.255v44H0z' />
        </clipPath>
      </defs>
    </svg>
  );
});

Logo.displayName = 'Logo';
